/**
 * The opening-cost confirmation workbench (batch 8 T11, #3032): summary, the unconfirmed layers, the
 * per-selection confirmation, and the reconcile preview. Exported ready to mount; T15 adds the tab.
 *
 * Rules the screen keeps:
 *  - read-only for a user who may not confirm: no checkboxes, no actions, and it says so;
 *  - selection is per layer and spans pages; the header checkbox selects only the rows on this page and
 *    there is no "confirm all": every selected layer needs its own cost in the dialog;
 *  - unknown stays unknown: the table never shows a cost for an unconfirmed layer, and hints are only
 *    shown to someone who may see values;
 *  - success is announced only for what the server accepted, and a refusal keeps the selection;
 *  - everything on the screen belongs to the signed-in owner (session, tenant, user and the confirm and
 *    financial rights): when any of them changes the body is replaced, so no selection, dialog, draft,
 *    result or cached cost of the previous owner is left, and a late answer of theirs has nowhere to land.
 *    The screen does not rely on its parent to remount it.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useNotify } from '@/stores/notificationStore';
import { useDebounce } from '@/hooks/useDebounce';
import { useWarehouses } from '@/hooks/useInventory';
import {
  useCostingSummary,
  useOpeningLayers,
  type ConfirmOutcome,
  type ConfirmRequest,
  type OpeningLayer,
} from '@/hooks/useInventoryCosting';
import { CostingSummaryCards } from './CostingSummaryCards';
import { OpeningCostConfirmDialog } from './OpeningCostConfirmDialog';
import { ReconcilePanel } from './ReconcilePanel';
import { formatEuroFromCents } from './costMoney';
import { ownerKey, useCostingAccess } from './costingSession';
import { useConfirmFlow } from './useConfirmFlow';

const PAGE_SIZES = [25, 50, 100] as const;
type Exhausted = 'include' | 'exclude' | 'only';

/** Layers named in a refusal's `details.layers`, with the consumed quantity the server now sees. */
function consumedFromRefusal(error: unknown): Map<string, number> {
  const result = new Map<string, number>();
  const data = (error as { data?: { details?: { layers?: { layerId?: unknown; consumedQuantity?: unknown }[] } } } | null)?.data;
  for (const entry of data?.details?.layers ?? []) {
    if (typeof entry.layerId === 'string' && typeof entry.consumedQuantity === 'number') result.set(entry.layerId, entry.consumedQuantity);
  }
  return result;
}

export function InventoryCostingWorkbench() {
  const { t } = useTranslation('inventoryCosting');
  const access = useCostingAccess();
  if (!access.canRead) return <Alert severity="warning">{t('access.denied')}</Alert>;
  return <WorkbenchBody key={ownerKey(access)} />;
}

function WorkbenchBody() {
  const { t, i18n } = useTranslation('inventoryCosting');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const language = i18n.language;
  const { canConfirm, canRead, canSeeValues } = useCostingAccess();

  const [warehouseId, setWarehouseId] = useState('');
  const [search, setSearch] = useState('');
  const [exhausted, setExhausted] = useState<Exhausted>('include');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [selected, setSelected] = useState<Record<string, OpeningLayer>>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [done, setDone] = useState<ConfirmOutcome | null>(null);

  const debouncedSearch = useDebounce(search.trim(), 400);
  useEffect(() => {
    setPage(0);
  }, [warehouseId, debouncedSearch, exhausted, includeInactive, pageSize]);

  const warehousesQuery = useWarehouses();
  const warehouses = useMemo(() => warehousesQuery.data?.data ?? [], [warehousesQuery.data]);
  const summary = useCostingSummary({ includeInactive, enabled: canRead });
  const layers = useOpeningLayers(
    { warehouseId: warehouseId || undefined, q: debouncedSearch || undefined, exhausted, includeInactive, limit: pageSize, offset: page * pageSize },
    { enabled: canRead },
  );
  const flow = useConfirmFlow();

  const items = useMemo(() => layers.data?.items ?? [], [layers.data]);
  const total = layers.data?.total ?? 0;
  // The keys already keep another owner's response away; this keeps a value off the screen even if one slipped in.
  const summaryShown = summary.data && !canSeeValues ? { ...summary.data, valuesVisible: false, value: null } : summary.data;
  // What the server says AND what this owner may see now: a downgrade hides values at once.
  const valuesVisible = canSeeValues && (layers.data?.valuesVisible ?? summary.data?.valuesVisible ?? false);

  // A refreshed page is the truth about the rows it contains (new consumption, a layer confirmed elsewhere).
  useEffect(() => {
    setSelected((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const row of items) {
        if (row.layerId in next) {
          next[row.layerId] = row;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [items]);

  // A refusal names the consumption the server now sees: show it, and the dialog asks for the acknowledgement again.
  useEffect(() => {
    const consumed = consumedFromRefusal(flow.refusal);
    if (consumed.size === 0) return;
    setSelected((prev) => {
      const next = { ...prev };
      for (const [id, quantity] of consumed) {
        const row = next[id];
        if (row) next[id] = { ...row, quantityConsumed: quantity };
      }
      return next;
    });
  }, [flow.refusal]);

  // The outcome of an accepted confirmation: announce exactly that, clear what it confirmed, close the dialog.
  useEffect(() => {
    const outcome = flow.outcome;
    if (!outcome) return;
    const confirmedIds = new Set(outcome.results.map((r) => r.layerId));
    setSelected((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !confirmedIds.has(id))));
    setDialogOpen(false);
    setDone(outcome);
    notify.success(
      t('notifications.confirmedTitle'),
      t('notifications.confirmedMessage', { count: outcome.results.length, value: formatEuroFromCents(outcome.total.remainingValue.cents, language) }),
    );
    flow.dismissOutcome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.outcome]);

  const selectedRows = Object.values(selected);
  const pageIds = items.map((row) => row.layerId);
  const pageSelected = pageIds.filter((id) => id in selected).length;
  const toggle = (row: OpeningLayer, checked: boolean) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (checked) next[row.layerId] = row;
      else delete next[row.layerId];
      return next;
    });
  const togglePage = (checked: boolean) =>
    setSelected((prev) => {
      const next = { ...prev };
      for (const row of items) {
        if (checked) next[row.layerId] = row;
        else delete next[row.layerId];
      }
      return next;
    });

  const submit = (request: ConfirmRequest) => {
    setDone(null);
    void flow.start(request);
  };

  const listError = layers.isError
    ? describeApiError(layers.error, tc, { codePrefix: 'inventoryCosting:errors', fallback: t('errors.loadFailed') })
    : null;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" component="h2" gutterBottom>
          {t('title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('intro')}
        </Typography>
      </Box>

      <CostingSummaryCards summary={summaryShown} loading={summary.isLoading} error={summary.isError ? summary.error : null} />

      {!canConfirm && <Alert severity="info">{t('access.readOnly')}</Alert>}
      {canConfirm && !valuesVisible && layers.data && <Alert severity="info">{t('access.valuesHidden')}</Alert>}

      {flow.discardedStored && (
        <Alert severity="warning" onClose={flow.dismissDiscardedStored}>
          {t('pending.discardedStored')}
        </Alert>
      )}
      {flow.storageUnreadable && <Alert severity="warning">{t('pending.storageUnreadable')}</Alert>}
      {flow.pending && !dialogOpen && (
        <Alert
          severity="warning"
          role="alert"
          action={
            <>
              <Button color="inherit" size="small" onClick={() => void flow.retry()} disabled={flow.phase === 'sending'}>
                {t('pending.retry')}
              </Button>
              <Button color="inherit" size="small" onClick={() => setDialogOpen(true)}>
                {t('pending.review')}
              </Button>
              <Button color="inherit" size="small" onClick={flow.discard} disabled={flow.phase === 'sending'}>
                {t('pending.discard')}
              </Button>
            </>
          }
        >
          <Typography variant="subtitle2">{t('pending.title')}</Typography>
          {flow.phase === 'sending' ? t('pending.sending') : flow.restored ? t('pending.restored') : t('pending.unknown')}{' '}
          {t('pending.sameRequest', { count: flow.pending.request.items.length })}
          {flow.persistence === 'unavailable' && <Box component="span"> {t('pending.notStored')}</Box>}
        </Alert>
      )}
      {done && (
        <Alert severity="success" onClose={() => setDone(null)} data-testid="confirm-done">
          {t('done.message', { count: done.results.length, value: formatEuroFromCents(done.total.remainingValue.cents, language), consumed: done.consumedUnitsAffected })}
          {done.total.roundingDifferenceCents !== 0 && (
            <Box component="span"> {t('done.rounding', { rows: formatEuroFromCents(done.total.rowRoundedSumCents, language), difference: done.total.roundingDifferenceCents })}</Box>
          )}
          {done.replayed && <Box component="span"> {t('done.replayed')}</Box>}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-labelledby="opening-layers-title">
        <Typography id="opening-layers-title" variant="h6" gutterBottom>
          {t('layers.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('layers.explain')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            label={t('filters.search')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: 220 }}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <TextField
            select
            size="small"
            label={t('filters.warehouse')}
            value={warehouseId}
            onChange={(event) => setWarehouseId(event.target.value)}
            sx={{ minWidth: 220 }}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          >
            <MenuItem value="">{t('filters.allWarehouses')}</MenuItem>
            {warehouses.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {w.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('filters.exhausted')}
            value={exhausted}
            onChange={(event) => setExhausted(event.target.value as Exhausted)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="include">{t('filters.exhaustedInclude')}</MenuItem>
            <MenuItem value="exclude">{t('filters.exhaustedExclude')}</MenuItem>
            <MenuItem value="only">{t('filters.exhaustedOnly')}</MenuItem>
          </TextField>
          <FormControlLabel
            control={<Switch checked={includeInactive} onChange={(event) => setIncludeInactive(event.target.checked)} />}
            label={t('filters.includeInactive')}
          />
        </Box>

        {layers.isFetching && <LinearProgress aria-label={tc('loading')} sx={{ mb: 1 }} />}
        {listError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={() => void layers.refetch()}>
                {tc('errors.refresh')}
              </Button>
            }
          >
            {listError}
          </Alert>
        )}

        {canConfirm && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }} aria-live="polite">
            <Typography variant="body2" data-testid="selection-count">
              {t('selection.count', { count: selectedRows.length })}
            </Typography>
            <Button variant="contained" disabled={selectedRows.length === 0} onClick={() => setDialogOpen(true)}>
              {t('selection.confirm', { count: selectedRows.length })}
            </Button>
            <Button disabled={selectedRows.length === 0} onClick={() => setSelected({})}>
              {t('selection.clear')}
            </Button>
          </Box>
        )}

        {!layers.isLoading && !listError && items.length === 0 ? (
          <Alert severity="success">{total === 0 ? t('layers.empty') : t('layers.emptyPage')}</Alert>
        ) : (
          <TableContainer>
            <Table size="small" aria-label={t('layers.tableLabel')}>
              <TableHead>
                <TableRow>
                  {canConfirm && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={pageSelected > 0 && pageSelected < pageIds.length}
                        checked={pageIds.length > 0 && pageSelected === pageIds.length}
                        onChange={(event) => togglePage(event.target.checked)}
                        slotProps={{ input: { 'aria-label': t('selection.selectPage') } }}
                      />
                    </TableCell>
                  )}
                  <TableCell>{t('table.part')}</TableCell>
                  <TableCell>{t('table.warehouse')}</TableCell>
                  <TableCell>{t('table.source')}</TableCell>
                  <TableCell align="right">{t('table.remaining')}</TableCell>
                  <TableCell align="right">{t('table.consumed')}</TableCell>
                  <TableCell>{t('table.cost')}</TableCell>
                  {valuesVisible && <TableCell>{t('table.hints')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.layerId} hover selected={row.layerId in selected}>
                    {canConfirm && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={row.layerId in selected}
                          onChange={(event) => toggle(row, event.target.checked)}
                          slotProps={{ input: { 'aria-label': t('selection.selectRow', { sku: row.sku }) } }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2">{row.sku}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.warehouseName}
                      {row.lineage.warehouseCount > 1 && (
                        <Tooltip title={row.lineage.remainingByWarehouse.map((w) => `${w.warehouseName}: ${String(w.quantityRemaining)}`).join(', ')}>
                          <Chip size="small" variant="outlined" sx={{ ml: 1 }} label={t('table.lineageWarehouses', { count: row.lineage.warehouseCount })} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{t(`source.${row.sourceType}`)}</TableCell>
                    <TableCell align="right">
                      {row.quantityRemaining}
                      {row.exhausted && <Chip size="small" sx={{ ml: 1 }} label={t('table.exhausted')} />}
                    </TableCell>
                    <TableCell align="right">
                      {row.quantityConsumed > 0 ? (
                        <Typography variant="body2" color="warning.main" component="span">
                          {row.quantityConsumed}
                        </Typography>
                      ) : (
                        row.quantityConsumed
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color="warning" variant="outlined" label={t('table.unknown')} />
                    </TableCell>
                    {valuesVisible && (
                      <TableCell>
                        {row.hints ? (
                          <Typography variant="caption" color="text.secondary">
                            {[
                              row.hints.lastReceipt ? t('table.hintLastReceipt') : null,
                              row.hints.catalogCost.status === 'recorded' ? t('table.hintCatalog') : null,
                              row.hints.preferredSupplier ? t('table.hintSupplier') : null,
                            ]
                              .filter((x): x is string => x !== null)
                              .join(' · ') || t('table.noHints')}
                          </Typography>
                        ) : null}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[...PAGE_SIZES]}
          onPageChange={(_event, next) => setPage(next)}
          onRowsPerPageChange={(event) => setPageSize(Number(event.target.value))}
          labelRowsPerPage={t('table.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('table.displayedRows', { from, to, count })}
        />
      </Paper>

      <ReconcilePanel warehouses={warehouses} canApply={canConfirm} />

      {canConfirm && (
        <OpeningCostConfirmDialog
          open={dialogOpen}
          rows={selectedRows}
          flow={flow}
          onClose={() => setDialogOpen(false)}
          onSubmit={submit}
          onRemoveRow={(id) => setSelected((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== id)))}
        />
      )}
    </Stack>
  );
}

export default InventoryCostingWorkbench;
